import React, { useState, useEffect } from 'react';
import {
  SimpleForm,
  TextInput,
  NumberInput,
  SelectInput,
  DateInput,
  required,
  useNotify,
  useRefresh,
  useDataProvider,
  Toolbar,
  SaveButton,
  useTranslate,
  DeleteButton,
} from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Box,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { EquipmentFileUpload } from '../EquipmentFileUpload';

const FuelToolbar = ({ isEdit, onDelete }: { isEdit?: boolean; onDelete?: () => void }) => {
  const translate = useTranslate();
  
  return (
    <Toolbar sx={{ 
      display: 'flex', 
      justifyContent: 'space-between',
      padding: '16px 24px',
      borderTop: '1px solid #e0e0e0'
    }}>
      <Box>
        {isEdit && onDelete && (
          <Button 
            onClick={onDelete} 
            color="error" 
            variant="outlined"
            sx={{ mr: 1 }}
          >
            {translate('ra.action.delete')}
          </Button>
        )}
      </Box>
      <SaveButton label={translate('ra.action.save')} />
    </Toolbar>
  );
};

interface FuelDialogProps {
  open: boolean;
  onClose: () => void;
  equipmentId: number;
  recordId?: number;
  isEdit?: boolean;
}

export const FuelDialog: React.FC<FuelDialogProps> = ({ 
  open, 
  onClose, 
  equipmentId,
  recordId,
  isEdit = false
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [files, setFiles] = useState([]);

  const fuelTypes = [
    { id: 'GASOLINE', name: 'Gasoline' },
    { id: 'DIESEL', name: 'Diesel' },
    { id: 'PROPANE', name: 'Propane' },
    { id: 'ELECTRIC', name: 'Electric' },
    { id: 'HYBRID', name: 'Hybrid' }
  ];

  // Load record for edit mode
  useEffect(() => {
    if (isEdit && recordId && open) {
      setLoading(true);
      dataProvider.getOne('equipment-fuel', { id: recordId })
        .then(({ data }) => {
          setRecord(data);
          // Load files if they exist
          if (data.files) {
            setFiles(data.files);
          }
        })
        .catch((error) => {
          console.error('Error loading fuel record:', error);
          notify('Error loading fuel record', { type: 'error' });
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (!isEdit) {
      setRecord(null);
      setFiles([]);
    }
  }, [isEdit, recordId, open, dataProvider, notify]);

  const handleSubmit = async (data: any) => {
    setLoading(true);
    try {
      const formattedData = {
        ...data,
        equipmentId: Number(equipmentId),
        files: files, // Include files in the submission
      };
      
      console.log('Submitting fuel data:', formattedData);
      
      if (isEdit && recordId) {
        await dataProvider.update('equipment-fuel', {
          id: recordId,
          data: formattedData,
          previousData: record || {}
        });
        notify('Fuel record updated successfully', { type: 'success' });
      } else {
        await dataProvider.create('equipment-fuel', {
          data: formattedData
        });
        notify('Fuel record created successfully', { type: 'success' });
      }
      
      refresh();
      onClose();
    } catch (error) {
      console.error('Fuel submission error:', error);
      notify(`Error ${isEdit ? 'updating' : 'creating'} fuel record: ${error.message || 'Unknown error'}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!recordId) return;
    
    setLoading(true);
    try {
      await dataProvider.delete('equipment-fuel', { id: recordId });
      notify(translate('resources.equipment-fuel.notifications.deleted'), { type: 'success' });
      refresh();
      onClose();
      setDeleteConfirmOpen(false);
    } catch (error) {
      console.error('Delete error:', error);
      notify(translate('resources.equipment-fuel.notifications.delete_error'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog 
        open={deleteConfirmOpen} 
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="sm"
      >
        <DialogTitle>{translate('resources.equipment_fuel.delete.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
            {translate('resources.equipment_fuel.delete.content')}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            {translate('ra.action.cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {translate('ra.action.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ position: 'relative', pr: 6 }}>
        {isEdit ? 'Edit Fuel Record' : 'Add Fuel Record'}
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <SimpleForm 
          onSubmit={handleSubmit} 
          record={record || { 
            fuelDate: new Date().toISOString().split('T')[0] 
          }}
          toolbar={<FuelToolbar isEdit={isEdit} onDelete={() => setDeleteConfirmOpen(true)} />}
          sx={{
            '& .RaSimpleForm-input': { width: '100%', maxWidth: '100%' },
            '& .MuiFormControl-root': { marginBottom: 0, width: '100%' }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
            <DateInput source="fuelDate" validate={[required()]} label="Fuel Date" variant="outlined" size="small" fullWidth />
            <SelectInput source="fuelType" choices={fuelTypes} validate={[required()]} label="Fuel Type" variant="outlined" size="small" fullWidth />
            <NumberInput source="quantity" validate={[required()]} label="Quantity" variant="outlined" size="small" fullWidth />
            <NumberInput source="unitCost" validate={[required()]} label="Unit Cost ($)" variant="outlined" size="small" fullWidth />
            <NumberInput source="totalCost" validate={[required()]} label="Total Cost ($)" variant="outlined" size="small" fullWidth />
            <NumberInput source="mileage" label="Odometer (KM)" variant="outlined" size="small" fullWidth />
            <TextInput source="location" label="Location" variant="outlined" size="small" fullWidth />
          </Box>
          {/* File Attachments Section - keep inside form so Save is at bottom */}
          <Box sx={{ mt: 3 }}>
            <EquipmentFileUpload
              entityType="fuel"
              entityId={isEdit ? recordId : undefined}
              files={files}
              onFilesChange={setFiles}
              title="Receipts & Documents"
            />
          </Box>
        </SimpleForm>
      </DialogContent>
    </Dialog>
    </>
  );
};